import { InfoFooter, InfoHeader } from "../../components/InfoLayout";
import { RelatedFreeTools, ToolFaq, toolPageSchemas } from "../../components/FreeToolSeo";
import { packages } from "../../config/pricing";
import usePageMeta from "../../hooks/usePageMeta";
import useUlezLookup from "./useUlezLookup";
import { ulezFaqsFor } from "../../content/ulezFaqs";
import { CLEAN_AIR_ZONES } from "../../../lib/vehicle/ulezChecker";
import { formatUkReg, isValidUkReg } from "../../../lib/vehicle/regValidation";
import infoCss from "../../styles/info.css?raw";
import ulezCss from "../../styles/ulez-sbr.css?raw";

const TFL_URL = "https://tfl.gov.uk/modes/driving/ultra-low-emission-zone";
const riskPlan = packages.find((plan) => plan.key === "risk") || packages[0];

const META_TITLE = "ULEZ Check by Reg: Is This Car ULEZ Free? | ServiceByReg";
const META_DESCRIPTION = "Free ULEZ check by registration. See whether a used car meets the London ULEZ standard before you view it, with the Euro standard and fuel it was judged on.";

const STANDARDS = [
  ["Petrol car or van", "Euro 4 or newer", "Usually 2006 onward. Some 2005 cars qualify, some do not."],
  ["Diesel car or van", "Euro 6", "Usually late 2015 onward. A few 2014 cars already met it."],
  ["Petrol motorbike", "Euro 3 or newer", "A lower bar than cars, so most bikes from 2007 are fine."],
  ["Electric or hydrogen", "Always compliant", "No tailpipe emissions, so no daily charge."],
  ["Hybrid", "Judged on its engine", "Checked against the petrol or diesel side, which normally passes."],
  ["Over 3,500kg", "London LEZ instead", "A separate zone with a much higher daily charge."],
];

function statusWord(status) {
  if (status === "compliant") return "ULEZ free";
  if (status === "charge") return "Charge likely";
  if (status === "lez") return "LEZ rules";
  return "Not settled";
}

function Evidence({ label, value }) {
  return (
    <div className="sbrz-evidence-row">
      <span>{label}</span>
      <strong>{value || "Not on record"}</strong>
    </div>
  );
}

function Verdict({ data }) {
  const { vehicle, ulez } = data;
  return (
    <section className={`sbrz-verdict is-${ulez.status}`} aria-live="polite">
      <div className="sbrz-verdict-head">
        <div className="sbrz-verdict-ref">
          <span>Registration</span>
          <strong>{formatUkReg(vehicle.registration)}</strong>
        </div>
        <div className="sbrz-stamp">{statusWord(ulez.status)}</div>
      </div>

      <h2>{ulez.headline}</h2>
      <p className="sbrz-verdict-detail">{ulez.detail}</p>

      <div className="sbrz-evidence">
        <span className="sbrz-evidence-title">What that was based on</span>
        <Evidence label="Vehicle" value={vehicle.make} />
        <Evidence label="Fuel" value={vehicle.fuelType} />
        <Evidence label="First registered" value={ulez.firstRegisteredYear} />
        <Evidence label="Euro standard" value={ulez.euroStandard ? `Euro ${ulez.euroStandard}` : null} />
        <Evidence label="CO2" value={vehicle.co2Emissions != null ? `${vehicle.co2Emissions} g/km` : null} />
      </div>

      <a className="sbrz-verify" href={TFL_URL} target="_blank" rel="noreferrer">
        Confirm this reg with Transport for London
      </a>
    </section>
  );
}

export default function ServiceByRegUlez({ brand }) {
  const { registration, setRegistration, data, error, loading, run } = useUlezLookup();
  const canonical = `https://${brand.domain}/ulez-checker`;

  usePageMeta({ title: META_TITLE, description: META_DESCRIPTION, canonical });

  const schema = {
    "@context": "https://schema.org",
    "@graph": toolPageSchemas({
      brand,
      canonical,
      name: `ULEZ Checker by Registration, ${brand.name}`,
      description: META_DESCRIPTION,
      alternateName: [
        "ULEZ checker",
        "ULEZ check by reg",
        "Is my car ULEZ compliant",
        "ULEZ compliance checker",
        "London ULEZ check",
        "Euro 6 diesel check",
      ],
      pathLabel: "ULEZ checker",
    }),
  };

  // With a checked reg the button goes to the Risk Check for it; without one it takes the
  // visitor back up to the reg box instead of promising "this vehicle".
  const hasReg = isValidUkReg(registration);
  const riskHref = hasReg ? `/checkout?vrm=${encodeURIComponent(registration)}&plan=risk` : "#ulez-registration";

  return (
    <>
      <style>{infoCss}</style>
      <style>{ulezCss}</style>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <InfoHeader brand={brand} />

      <main className="sbrz">
        <section className="sbrz-hero">
          <div className="sbrz-hero-inner">
            <div className="sbrz-hero-copy">
              <span className="sbrz-kicker">Free ULEZ check</span>
              <h1>Is this car ULEZ free?</h1>
              <p>
                Check the seller&apos;s registration before you go and see it. We read the
                vehicle&apos;s own fuel type and Euro standard, so you are not relying on
                what the advert claims.
              </p>
              <ul className="sbrz-hero-points">
                <li>No account, no payment</li>
                <li>Works on any UK plate</li>
                <li>Shows the evidence, not just a badge</li>
              </ul>
            </div>

            <form
              className="sbrz-form"
              onSubmit={(event) => { event.preventDefault(); run(registration); }}
              noValidate
            >
              <label htmlFor="ulez-registration">Enter the registration</label>
              <div className="sbrz-plate">
                <span className="sbrz-plate-band" aria-hidden="true">
                  <img src="/gb-flag.svg" alt="" />
                  UK
                </span>
                <input
                  id="ulez-registration"
                  value={registration}
                  maxLength="8"
                  autoComplete="off"
                  inputMode="text"
                  placeholder="ENTER REG"
                  aria-describedby="ulez-help ulez-error"
                  onChange={(event) => setRegistration(event.target.value)}
                />
              </div>
              <button type="submit" disabled={loading}>
                {loading ? "Checking the record..." : "Check ULEZ status"}
              </button>
              <small id="ulez-help">We use the reg to look the vehicle up. Nothing is stored against you.</small>
              <div className="sbrz-error" id="ulez-error" role="alert">{error}</div>
            </form>
          </div>
        </section>

        <div className="sbrz-body">
          {loading && <div className="sbrz-loading" aria-live="polite"><span />Reading the vehicle record...</div>}
          {data && <Verdict data={data} />}

          <section className="sbrz-answer">
            <span className="sbrz-section-kicker">Straight answer</span>
            <h2>How do I know if a car is ULEZ compliant?</h2>
            <p>
              A car is ULEZ compliant if it meets the Euro emissions standard for its fuel.
              Petrol needs Euro 4, which normally means first registered from 2006. Diesel needs
              Euro 6, which normally means late 2015. Everything fully electric is compliant.
              Anything that misses its standard pays £12.50 for every day it is driven inside
              the zone, and the zone covers every London borough.
            </p>
            <a className="sbrz-guide-link" href="/guides/what-a-reg-check-shows">
              See what a basic reg check does and does not tell you
            </a>
          </section>

          <section className="sbrz-standards">
            <div className="sbrz-standards-head">
              <span className="sbrz-section-kicker">The standards</span>
              <h2>What each vehicle has to meet</h2>
            </div>
            <div className="sbrz-standards-table">
              {STANDARDS.map(([type, needs, note]) => (
                <div className="sbrz-standard" key={type}>
                  <div className="sbrz-standard-type">{type}</div>
                  <div className="sbrz-standard-needs">{needs}</div>
                  <div className="sbrz-standard-note">{note}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="sbrz-zones">
            <div className="sbrz-standards-head">
              <span className="sbrz-section-kicker">Where it applies</span>
              <h2>UK zones that charge private cars</h2>
            </div>
            <div className="sbrz-zone-grid">
              {CLEAN_AIR_ZONES.map((zone) => (
                <article className="sbrz-zone" key={zone.key}>
                  <h3>{zone.name}</h3>
                  <span className="sbrz-zone-area">{zone.area}</span>
                  <strong>{zone.charge}</strong>
                  <a href={zone.checkUrl} target="_blank" rel="noreferrer">{zone.checkLabel}</a>
                </article>
              ))}
            </div>
            <p className="sbrz-zones-note">
              Bradford, Portsmouth, Newcastle, Sheffield and Tyneside run clean air zones that
              do not charge private cars. Our checker uses the London rules, which are the
              strictest of the charging zones.
            </p>
          </section>

          <section className="sbrz-bridge">
            <div className="sbrz-bridge-copy">
              <span className="sbrz-section-kicker">Before you put down a deposit</span>
              <h2>ULEZ tells you the running cost. It tells you nothing about the risk.</h2>
              <p>
                A ULEZ free car can still be on outstanding finance, be a recorded write-off,
                be showing a mileage that has been wound back, or be reported stolen. Those are
                the things that cost a buyer thousands, and none of them show up in an
                emissions check.
              </p>
            </div>
            <div className="sbrz-bridge-offer">
              <strong>Vehicle Risk Check</strong>
              <ul>
                <li>Outstanding finance</li>
                <li>Stolen vehicle markers</li>
                <li>Insurance write-offs</li>
                <li>Recorded mileage checks</li>
              </ul>
              <div className="sbrz-bridge-price">
                <span>One payment. No subscription.</span>
                <b>£{riskPlan.plate}</b>
              </div>
              <a href={riskHref} onClick={hasReg ? undefined : () => document.getElementById("ulez-registration")?.focus()}>{hasReg ? "Check this vehicle" : "Enter a reg to check"}</a>
            </div>
          </section>

          <ToolFaq title="ULEZ questions buyers ask" items={ulezFaqsFor(brand.key)} />
          <RelatedFreeTools current="ulez" />

          <aside className="sbrz-disclaimer">
            <strong>This is a guide, not the final word.</strong>
            <span>
              We work from the free registration record. Transport for London runs its own
              database and has the final say on any single vehicle, so confirm the reg with
              TfL before you drive into the zone or agree a price.
            </span>
          </aside>
        </div>
      </main>

      <InfoFooter brand={brand} />
    </>
  );
}
